import type { WeeklyPlannerPayload, WeeklyPlannerRow } from "@/lib/api";
import { reorderWithEdge } from "@/lib/drag-reorder";

type PlannerRowMeta = {
  row_kind: "project" | "roll_up_group";
  row_entity_id: number;
  project_group_id?: number;
};

type PlannerRowsById = Record<string, PlannerRowMeta>;

export function projectIdsByGroup(rows: WeeklyPlannerRow[]): Record<number, number[]> {
  const groupedProjectIds: Record<number, number[]> = {};
  let currentGroupId: number | null = null;

  rows.forEach((row) => {
    if (row.row_kind === "group_heading") {
      currentGroupId = row.row_entity_id;
      groupedProjectIds[currentGroupId] = groupedProjectIds[currentGroupId] || [];
      return;
    }

    if (row.row_kind === "project" && currentGroupId !== null) {
      groupedProjectIds[currentGroupId] ||= [];
      groupedProjectIds[currentGroupId].push(row.row_entity_id);
    }
  });

  return groupedProjectIds;
}

export function canDropProjectRow(args: {
  plannerRowsById?: PlannerRowsById;
  sourceRowId: string;
  targetRowId: string;
}): boolean {
  if (!args.plannerRowsById || args.sourceRowId === args.targetRowId) return false;

  const sourceRow = args.plannerRowsById[args.sourceRowId];
  const targetRow = args.plannerRowsById[args.targetRowId];
  if (!sourceRow || !targetRow) return false;
  if (sourceRow.row_kind !== "project" || targetRow.row_kind !== "project") return false;
  return Boolean(
    sourceRow.project_group_id && sourceRow.project_group_id === targetRow.project_group_id,
  );
}

export function nextProjectOrderForDrop(args: {
  plannerRows: WeeklyPlannerRow[];
  plannerRowsById?: PlannerRowsById;
  sourceRowId: string;
  targetRowId: string;
  targetEdge: "top" | "bottom";
}): { projectGroupId: number | null; nextOrder: number[]; changed: boolean } {
  if (
    !canDropProjectRow({
      plannerRowsById: args.plannerRowsById,
      sourceRowId: args.sourceRowId,
      targetRowId: args.targetRowId,
    })
  ) {
    return { projectGroupId: null, nextOrder: [], changed: false };
  }

  const sourceRow = args.plannerRowsById?.[args.sourceRowId];
  const targetRow = args.plannerRowsById?.[args.targetRowId];
  if (!sourceRow || !targetRow || sourceRow.row_kind !== "project" || targetRow.row_kind !== "project") {
    return { projectGroupId: null, nextOrder: [], changed: false };
  }

  const currentOrder = projectIdsByGroup(args.plannerRows)[sourceRow.project_group_id || -1] || [];
  const reorderResult = reorderWithEdge({
    orderedIds: currentOrder,
    sourceId: sourceRow.row_entity_id,
    targetId: targetRow.row_entity_id,
    targetEdge: args.targetEdge,
  });

  return {
    projectGroupId: sourceRow.project_group_id || null,
    nextOrder: reorderResult.nextOrder,
    changed: reorderResult.changed,
  };
}

export function nextDeveloperOrderForDrop(args: {
  planner: WeeklyPlannerPayload;
  sourceDeveloperId: number;
  targetDeveloperId: number;
  targetEdge: "left" | "right";
}): { nextOrder: number[]; changed: boolean } {
  const currentOrder = args.planner.developers.map((developer) => developer.id);
  return reorderWithEdge({
    orderedIds: currentOrder,
    sourceId: args.sourceDeveloperId,
    targetId: args.targetDeveloperId,
    targetEdge: args.targetEdge,
  });
}
